import { Divider } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNotification } from 'src/context/NotificationContext';
import styled from 'styled-components';
import { useCall } from '../../customer-call-sdk';
import useCountdown from '../../hooks/useCountdown';
import Button from '../common/Button';
import TextButton from '../common/TextButton';
import UserAvatar from '../common/UserAvatar';

const StyledOutboundCallJoin = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    padding: 1.5rem;
    overflow-y: auto;

    .header,
    .footer {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .heading {
        color: var(--text-primary);
        font-size: 2.125rem;
        font-weight: bold;
        line-height: 2.5rem;
        text-align: center;
        margin-bottom: 1rem;
    }
    .calling-label {
        color: rgba(17, 17, 17, 0.6);
        font-size: 1rem;
        font-weight: 600;
        margin-bottom: 0.5rem;
    }
    .phone-number {
        color: var(--text-primary);
        font-size: 1.75rem;
        font-weight: bold;
        margin-bottom: 0.5rem;
    }
    .divider {
        width: 100%;
        margin: 0 -1.5rem 1rem;
        padding: 0 1.5rem;
    }
    .video-interpreter-label {
        color: rgba(17, 17, 17, 0.6);
        font-size: 1.2rem;
        font-weight: 600;
        margin-bottom: 1rem;
    }
    .avatar-container {
        margin-bottom: 1.5rem;
        .avatar {
            width: 100px;
            height: 100px;
        }
        .avatar svg {
            width: 70px;
            height: 70px;
        }
    }
    .interpreter-name {
        color: var(--text-primary);
        font-size: 1.75rem;
        font-weight: bold;
        margin-bottom: 0.5rem;
    }
    .interpreter-number {
        font-size: 1rem;
        margin-bottom: 3rem;
    }
    .auto-joining-text {
        color: var(--text-primary);
        font-weight: bold;
        margin-bottom: 0.5rem;
    }
    .join-btn {
        margin-bottom: 0.5rem;
    }
`;

export interface OutboundCallJoinProps extends React.HTMLAttributes<HTMLDivElement> {}

const OutboundCallJoin: React.FC<OutboundCallJoinProps> = ({ ...props }) => {
    const call = useCall();
    const { showNotification, closeNotifications } = useNotification();
    const { interpreterName, calleeNumber, requestType, calleeName } = call;
    const { countdown, startTimer, stopTimer } = useCountdown(5);
    const [isLoading, setIsLoading] = useState(false);

    const isVRS = requestType === 'VRS_OUTBOUND';
    const isVRI = requestType === 'VRI';
    const isCustomerService = requestType === 'P2P_OUTBOUND';

    const getHeading = () => {
        switch (requestType) {
            case 'VRI':
                return 'Your interpreter is ready.';
            case 'VRS_OUTBOUND':
                return 'Your VRS call request is ready!';
            case 'P2P_OUTBOUND':
                return 'Your call request is ready!';
            default:
                return '';
        }
    };

    const handleJoinClick = async () => {
        setIsLoading(true);
        await call.join();
        setIsLoading(false);
    };

    const handleCancelClick = async () => {
        setIsLoading(true);
        await call.reject();
        setIsLoading(false);
    };

    useEffect(() => {
        if (countdown === 0) {
            call.join();
        }
    }, [countdown]);

    const heading = getHeading();

    useEffect(() => {
        startTimer();
        showNotification(heading, 'Join the call', true);
        return () => {
            stopTimer();
            closeNotifications();
        };
    }, []);

    return (
        <StyledOutboundCallJoin {...props}>
            <div className="header">
                <h3 className="heading">{heading}</h3>
                {(isVRS || isCustomerService) && (
                    <>
                        <p className="calling-label">You&apos;ll be calling</p>
                        <div className="phone-number">{calleeName || calleeNumber}</div>
                        <Divider className="divider" />
                    </>
                )}
                {(isVRI || isVRS) && (
                    <p className="video-interpreter-label">Your video interpreter</p>
                )}

                <div className="avatar-container">
                    <UserAvatar className="avatar" />
                </div>
                {(isVRI || isVRS) && (
                    <>
                        <div className="interpreter-name">{interpreterName}</div>
                        {/* <div className="interpreter-number">{interpreterNRPCDNumber}</div> */}
                    </>
                )}
            </div>
            <div className="footer">
                <div className="auto-joining-text">{`Auto-joining in ${countdown} sec...`}</div>
                <Button
                    fullWidth
                    size="large"
                    className="join-btn capitalize"
                    onClick={handleJoinClick}
                    disabled={isLoading}
                    aria-label="Join"
                >
                    Join
                </Button>
                <TextButton
                    color="secondary"
                    onClick={handleCancelClick}
                    disabled={isLoading}
                    className="capitalize"
                    aria-label="Cancel"
                >
                    Cancel
                </TextButton>
            </div>
        </StyledOutboundCallJoin>
    );
};

export default OutboundCallJoin;
